import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, Volume2, VolumeX } from "lucide-react";
import { track } from "@/lib/analytics";

/**
 * FloatingFilmChip — small floating "now playing" video bubble pinned to the
 * bottom-right. Plays the brand film silently, intermittently (10s on / 6s off)
 * so visitors notice motion in their peripheral vision without it becoming
 * tedious. Tapping it expands the chip to a larger preview with sound; tapping
 * the expanded preview scrolls to the full FilmSection.
 *
 * Behaviour:
 *   - Renders ONLY on `/` (homepage) — caller decides via `enabled` prop.
 *   - Dismissible via the close (×) button; remembers dismissal in localStorage
 *     for the rest of the session so we never nag returning visitors.
 *   - On first reveal: a 1.5s delay + scale-in animation so it feels organic.
 *   - Auto-hides if the user is already scrolled near the FilmSection
 *     (no point showing both at once).
 */
const DISMISS_KEY = "am_floating_film_dismissed";

const FloatingFilmChip = ({ enabled = true }) => {
  const videoRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [muted, setMuted] = useState(true);
  const [visible, setVisible] = useState(false);
  const [nearFilm, setNearFilm] = useState(false);
  const [playingNow, setPlayingNow] = useState(true);

  // Respect prior dismissal so it doesn't reappear on every nav.
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(DISMISS_KEY) === "1") return;
    const t = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(t);
  }, [enabled]);

  // Auto-hide when the main FilmSection scrolls into view — they don't both need
  // to compete for attention.
  useEffect(() => {
    if (!visible) return;
    const main = document.querySelector("[data-testid=film-section]");
    if (!main) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => setNearFilm(e.isIntersecting));
    }, { threshold: 0.15 });
    io.observe(main);
    return () => io.disconnect();
  }, [visible]);

  // Intermittent playback: 10s play / 6s pause loop. Keeps motion eye-catching
  // without being constant noise. Works while video is mounted.
  useEffect(() => {
    if (!visible) return;
    const v = videoRef.current;
    if (!v) return;
    let mounted = true;
    let timer;
    const cycle = (phase) => {
      if (!mounted) return;
      if (phase === "play") {
        v.play().then(() => setPlayingNow(true)).catch(() => {});
        timer = setTimeout(() => cycle("pause"), 10000);
      } else {
        v.pause();
        setPlayingNow(false);
        timer = setTimeout(() => cycle("play"), 6000);
      }
    };
    cycle("play");
    return () => { mounted = false; clearTimeout(timer); };
  }, [visible]);

  const dismiss = () => {
    track("film_chip_dismissed");
    sessionStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
  };

  const handleExpand = () => {
    track("film_chip_expanded");
    setExpanded(true);
    setMuted(false);
    const v = videoRef.current;
    if (v) { v.muted = false; v.play().catch(() => {}); }
  };

  const handleCollapse = () => {
    setExpanded(false);
    setMuted(true);
    const v = videoRef.current;
    if (v) v.muted = true;
  };

  const scrollToFull = () => {
    track("film_chip_full_film_click");
    document.querySelector("[data-testid=film-section]")?.scrollIntoView({ behavior: "smooth", block: "start" });
    handleCollapse();
  };

  if (!enabled || !visible || nearFilm) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="film-chip"
        data-testid="floating-film-chip"
        initial={{ opacity: 0, scale: 0.4, y: 40 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.4, y: 40 }}
        transition={{ type: "spring", damping: 18, stiffness: 220 }}
        className="fixed bottom-5 right-5 z-40 sm:bottom-6 sm:right-6"
      >
        {/* COLLAPSED — small circle */}
        {!expanded && (
          <button
            type="button"
            onClick={handleExpand}
            data-testid="film-chip-collapsed"
            className="group relative w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden ring-4 ring-white shadow-[0_15px_40px_-10px_rgba(0,0,0,0.35)] hover:ring-[#C05A3C]/40 hover:scale-105 transition-all"
            aria-label="Watch our story"
          >
            <video
              ref={videoRef}
              src="/ads/afrimarket-film.mp4"
              className="w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
            />
            {/* Dark overlay + play icon */}
            <div className="absolute inset-0 bg-black/15 grid place-items-center group-hover:bg-black/35 transition-colors">
              <div className="w-10 h-10 rounded-full bg-white/95 grid place-items-center shadow-md group-hover:scale-110 transition-transform">
                <Play className="w-4 h-4 fill-[#C05A3C] text-[#C05A3C] ml-[1px]" />
              </div>
            </div>
            {/* Pulsing "Our story" pill */}
            <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-[#C05A3C] text-white text-[9px] font-semibold uppercase tracking-wider whitespace-nowrap shadow-md">
              {playingNow ? "Live" : "Paused"} · 60s
            </div>
            {/* Pulsing ring while playing */}
            {playingNow && (
              <span className="absolute inset-0 rounded-full ring-2 ring-[#C05A3C]/40 animate-ping pointer-events-none" aria-hidden />
            )}
          </button>
        )}

        {/* EXPANDED — larger card */}
        {expanded && (
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative w-[280px] sm:w-[340px] aspect-video rounded-xl overflow-hidden bg-black shadow-[0_25px_60px_-15px_rgba(0,0,0,0.5)] ring-1 ring-white/10"
            data-testid="film-chip-expanded"
          >
            <video
              src="/ads/afrimarket-film.mp4"
              className="w-full h-full object-cover"
              autoPlay
              loop
              playsInline
              preload="auto"
              ref={(el) => { if (el) { el.muted = muted; videoRef.current = el; } }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-black/35 pointer-events-none" aria-hidden />
            <button
              type="button"
              data-testid="film-chip-mute"
              onClick={() => {
                const v = videoRef.current; if (!v) return;
                v.muted = !v.muted; setMuted(v.muted);
                if (!v.muted) track("film_chip_unmuted");
              }}
              className="absolute top-2 left-2 w-8 h-8 rounded-full bg-white/15 hover:bg-white/30 grid place-items-center text-white backdrop-blur-md"
              aria-label={muted ? "Unmute" : "Mute"}
            >
              {muted ? <VolumeX className="w-4 h-4"/> : <Volume2 className="w-4 h-4"/>}
            </button>
            <button
              type="button"
              data-testid="film-chip-collapse"
              onClick={handleCollapse}
              className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/15 hover:bg-white/30 grid place-items-center text-white backdrop-blur-md"
              aria-label="Minimise"
            >
              <X className="w-4 h-4" />
            </button>
            <button
              type="button"
              data-testid="film-chip-fullsize"
              onClick={scrollToFull}
              className="absolute bottom-2 left-2 right-2 h-9 rounded-full bg-white/95 hover:bg-white text-[#1A1A1A] text-xs font-semibold flex items-center justify-center gap-2 shadow-md"
            >
              <Play className="w-3.5 h-3.5 fill-[#C05A3C] text-[#C05A3C]" /> Watch full film
            </button>
          </motion.div>
        )}

        {/* Dismiss (×) — only on collapsed state */}
        {!expanded && (
          <button
            type="button"
            data-testid="film-chip-dismiss"
            onClick={dismiss}
            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white shadow-md text-[#1A1A1A] grid place-items-center hover:bg-[#C05A3C] hover:text-white transition-colors ring-1 ring-black/10"
            aria-label="Dismiss"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default FloatingFilmChip;
